import { set } from 'react-hook-form';
import type { TProductGroupType } from '@/features/Settings/RolesAndPermission/utils';
import type {
  INotificationPreference,
  INotificationPreferenceTypes,
  NotificationPreferencesForm,
} from '@/pages/Settings/NotificationPreferences/types';
import type { IGetNotificationPreferencesServiceResData, IPutNotificationPreferencesPayload } from '../../../api/Notification/types';

export const getFormFieldPath = (
  data: IGetNotificationPreferencesServiceResData,
  notificationGroup: INotificationPreference,
  notificationType: INotificationPreferenceTypes
) => {
  return `${data.notificationFor}.${data.notificationGroup}.ID_${notificationGroup.id}.${notificationType.slug}`;
};

export const getFormFieldPathArray = (
  data: IGetNotificationPreferencesServiceResData,
  notificationGroup: INotificationPreference,
  notificationType: INotificationPreferenceTypes
) => {
  return [data.notificationFor, data.notificationGroup, notificationGroup.id, notificationType.slug];
};

export const transformGetNotificationPreferencesResData = (notificationPreferences: IGetNotificationPreferencesServiceResData[]) => {
  const flatNotificationPreferencesResData = notificationPreferences?.reduce(
    (acc: INotificationPreference[], next: IGetNotificationPreferencesServiceResData) => {
      return [...acc, ...next.notifications];
    },
    []
  );
  const notificationsList: IPutNotificationPreferencesPayload[] = flatNotificationPreferencesResData?.map((fnp) => {
    const flatNtfTypes = fnp.types.filter((nt) => {
      if (fnp.canBeTurnedOff) {
        return nt.enabled;
      } else {
        return nt;
      }
    });

    return {
      notificationId: fnp.id,
      notificationTypes: flatNtfTypes.map((n) => n.slug).sort(),
    };
  });

  return notificationsList;
};

export const convertNotificationPreferencesToFormState = (data: IGetNotificationPreferencesServiceResData[]) => {
  const initialState: NotificationPreferencesForm = {};

  const checkedCount: Record<TProductGroupType, Record<string, number>> = {
    SHERPA: {
      EMAIL: 0,
      IN_APP: 0,
    },
    TERMINAL: {
      EMAIL: 0,
      IN_APP: 0,
    },
    ROVER: {
      EMAIL: 0,
      IN_APP: 0,
    },
  };
  const totalCount: Record<TProductGroupType, Record<string, number>> = {
    SHERPA: {
      EMAIL: 0,
      IN_APP: 0,
    },
    TERMINAL: {
      EMAIL: 0,
      IN_APP: 0,
    },
    ROVER: {
      EMAIL: 0,
      IN_APP: 0,
    },
  };

  data.forEach((appNotifications) => {
    const { notificationFor, notificationGroup, notifications } = appNotifications;

    notifications.forEach((notification) => {
      notification.types.forEach((type) => {
        set(initialState, getFormFieldPath(appNotifications, notification, type), type.enabled);

        const appName = notificationFor.toUpperCase() as TProductGroupType;

        if (type.enabled) {
          checkedCount[appName][type.slug]++;
        }

        totalCount[appName][type.slug]++;
      });
    });

    // Update all checkbox state for each tab
    Object.keys(initialState).forEach((notificationFor) => {
      const tabName = notificationFor.toUpperCase() as TProductGroupType;
      const isAllEmailChecked = checkedCount[tabName].EMAIL === totalCount[tabName].EMAIL;
      const isAllInAppChecked = checkedCount[tabName].IN_APP === totalCount[tabName].IN_APP;

      set(initialState, `${notificationFor}.all`, {
        EMAIL: isAllEmailChecked,
        IN_APP: isAllInAppChecked,
      });
    });
  });

  return {
    initialState,
  };
};

export const createPutPayload = (values: NotificationPreferencesForm): IPutNotificationPreferencesPayload[] => {
  const payload: IPutNotificationPreferencesPayload[] = [];

  Object.entries(values).forEach((tabNotificationGroups) => {
    Object.entries(tabNotificationGroups[1]).forEach((notificationGroup) => {
      if (notificationGroup[0] === 'all') return;
      Object.entries(notificationGroup[1]).forEach((notification) => {
        const allTypes = values[tabNotificationGroups[0]][notificationGroup[0]][notification[0]];

        payload.push({
          notificationId: Number(notification[0].split('_')[1]), // Parse ID from string "ID_36",
          notificationTypes: Object.keys(allTypes).filter((type) => allTypes[type as 'EMAIL' | 'IN_APP']),
        });
      });
    });
  });

  return payload;
};

// Check if all notifications in current tab in specific notification type  are enabled
export const isAllChecked = (currentTab: string, type: 'EMAIL' | 'IN_APP', allNotifications: NotificationPreferencesForm): boolean => {
  const notificationGroup = allNotifications[currentTab];

  return Object.keys(notificationGroup)
    .filter((groupKey) => groupKey !== 'all')
    .every((groupKey) =>
      Object.values(notificationGroup[groupKey]).every(
        (notification: { EMAIL: boolean; IN_APP: boolean }) => notification[type as 'EMAIL' | 'IN_APP'] === true
      )
    );
};
